import React, { useState } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import Pagination from 'react-ultimate-pagination-bootstrap-4';
import queryString from 'query-string';
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut';

import { billingReports, exportableReportConfigs } from 'ducks';
import RadioFilter from 'components/shared/RadioFilter';
import withFiltering from '../../hocs/withFiltering';
import withPagination from '../../hocs/withPagination';
import withDateFilter from '../../hocs/withDateFilter';
import { duckify, filterDuck, singleDuck } from '../../utils/duckify';
import BulkAction from '../ContactList/BulkActions/BulkAction';
import BulkExportAction from '../ContactList/BulkActions/BulkExportAction';
import BulkActions from '../ContactList/BulkActions/BulkActions';
import DateFilter from '../shared/DateFilter';
import Filters from '../Filters/Filters';
import Placeholder from '../Placeholder';
import GroupedList from '../ContactList/GroupedList';
import history from '../../shared_history';

import './styles/BillingReports.scss';

// this is the method to parse the object of possible columns that we get from the server.  We get all possible
// column configurations when we load the page, but there are certain types of columns that require extra setup, mainly
// ones with 'restriction' (show or hide column) and 'reload_columns_with_data' (columns are variable, like dates, and
// change based on the data.

function getColumns(exportableReportConfigs, billingReports, breakdown) {
  const columnKeys = Object.keys(exportableReportConfigs.columns);
  const returnColumns = {};

  columnKeys.forEach((columnGroup) => {
    const tiers = exportableReportConfigs.columns[columnGroup];
    returnColumns[columnGroup] = [];
    for (let tier = 0; tier < tiers.length; tier++) {
      returnColumns[columnGroup][tier] = [];
      const fields = tiers[tier];
      fields.forEach((field) => {
        if ((field.breakdown_only !== undefined && breakdown === true) || field.breakdown_only === undefined) {
          if ((billingReports.length > 0) && (field.reload_columns_with_data)) {
            Object.keys(billingReports[0][field.id]).forEach((key) => {
              const newColumn = {
                header: key,
                column_type: field.column_type,
                id: `${field.id}__${key}`,
                parentArray: field.id
              };
              returnColumns[columnGroup][tier].push(newColumn);
            });
          } else {
            returnColumns[columnGroup][tier].push(field);
          }
        }
      });
    }
  });

  return returnColumns;
}

const BillingReport = ({
  billingReports,
  filters,
  page,
  setFilters,
  setPage,
  dateFilter,
  exportableReportConfigs,
  allBillingReports,
  allDucksReady
}) => {
  const { total, perPage } = billingReports;
  /* TODO in SA-24754 pagination not supported. Also, total is currently not a number. const totalPages = Math.ceil(total / perPage); */
  const uriGrouping = new URL(location.href).searchParams.get('grouping');
  const query = queryString.parse(location.search);
  const [breakdown, setBreakdown] = useState(query['breakdown'] === 'true' || false);

  const currentReport = new URL(location.href).searchParams.get('report');// || allBillingReports[0].id;

  let filteredColumns = {};
  let currentGrouping;
  let count = 0;
  let itemsName = '';

  const header = (<h2><a href={'/admin/billing/billing_reports'}>Billing Reports</a></h2>);
  const reportCards = [];
  const reportList = [];

  allBillingReports.forEach((report) => {
    const link = `/admin/billing/billing_reports?report=${report.id}`;
    reportCards.push(
      <a href={link}>
        <div className={'billing-report--card'}>
          <div className={'billing-report--card--header'}>
            <span>{report.name}</span>
          </div>
          <div className={'billing-report--card--body'}>
            <span>{report.description}</span>
          </div>
        </div>
      </a>
    );
    reportList.push(
      <div
        className={`report-titles${exportableReportConfigs.match_id === report.id ? ' selected' : ''}`}
      >
        <a id={report.id} href={link}>{report.name}</a>
      </div>
    );
  });

  if (currentReport === null) {
    return (
      <div>
        {header}
        {reportCards}
      </div>
    );
  }

  if (allDucksReady.ready && exportableReportConfigs.error_message !== undefined) {
    return (
      <div>
        {exportableReportConfigs.error_message}
      </div>
    );
  }

  if (billingReports.ready && exportableReportConfigs !== {} && exportableReportConfigs.columns !== undefined) {
    currentGrouping = uriGrouping === null ? Object.keys(exportableReportConfigs.columns)[0] : uriGrouping;
    [count, itemsName] = billingReports.total.split(' ');
    filteredColumns = getColumns(exportableReportConfigs, billingReports, breakdown);
  }

  const updateUrl = (param, newOption) => {
    const searchParams = { ...query };
    searchParams[param] = newOption;

    history.push({
      ...location,
      search: `?${queryString.stringify(searchParams)}`
    });
  };

  const setBreakdownAndSubmit = (newOption) => {
    setBreakdown(newOption);
    updateUrl('breakdown', newOption);
  };

  return (
    <div>
      <div>
        <div>
          <div className='billing-reports-space-between'>
            {header}
            {!billingReports.error && (
              <BulkActions>
                <BulkAction title='Download Report' icon={faSignOut}>
                  <BulkExportAction
                    type={currentReport}
                    location={location}
                    dateFilter={dateFilter}
                    url='/api/billing_reports/export.csv'
                  />
                  <BulkExportAction
                    title='Export to PDF'
                    type={currentReport}
                    location={location}
                    dateFilter={dateFilter}
                    url='/api/billing_reports/export.pdf'
                  />
                </BulkAction>
              </BulkActions>
            )}
          </div>
          {reportList}
          <br clear='all'/>
        </div>
        <div className='billing-report-modifiers'>
          {exportableReportConfigs.allowed_groupings && (
            <div className='grouping'>
              <RadioFilter
                location={location}
                label='Show Report By'
                fieldName='grouping'
                defaultOption='account_id'
                model='grouping'
                options={exportableReportConfigs.allowed_groupings}
                hideSubmit
                vertical
              />
            </div>
          )}
          {exportableReportConfigs.time_grouping && (
            <div className='date-grouping'>
              <RadioFilter
                location={location}
                label='Day or Month'
                fieldName='date_grouping'
                defaultOption='month'
                model='date_grouping'
                options={{day: 'Day', month: 'Month'}}
                hideSubmit
                vertical
              />
            </div>
          )}
          {exportableReportConfigs.date_filter_label && (
            <DateFilter
              useAll={exportableReportConfigs.use_all_date_filter}
              multiline
              className='date-range'
              label={exportableReportConfigs.date_filter_label}
              dateFilter={dateFilter}
            />
          )}
          {exportableReportConfigs.breakdown && (
            <div className='breakdown'>
              <label>View</label>
              <br/>
              <input
                type='checkbox'
                id='breakdown'
                name='breakdown'
                checked={breakdown}
                className='form-check-input'
                onChange={() => setBreakdownAndSubmit(!breakdown)}
              />
              Charge, Credit, and Payment Breakdown
            </div>
          )}
        </div>
      </div>

      <div className='billing-reports-space-between'>
        <Filters
          fields={exportableReportConfigs.filters !== undefined ? exportableReportConfigs.filters : []}
          count={count}
          selected={filters}
          submitFilters={setFilters}
          itemsName={itemsName}
        />

        {/* TODO in SA-24754 pagination not supported totalPages > 1 && billingReports.ready && (
          <div className='contact-list-react-pagination br-pagination'>
            <div className='pagination'>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                hideFirstAndLastPageLinks
                onChange={setPage}
              />
            </div>
          </div>
        )*/}
      </div>

      <div>
        <Placeholder.invisible ready={billingReports.ready}>
          <table className='table'/>
        </Placeholder.invisible>

        <div className='scrollable'>
          <GroupedList
            className='billing-report-item-list'
            items={billingReports}
            columns={filteredColumns}
            emptyMessage='There are no billing items matching the filters provided.'
            grouping={currentGrouping}
          />
        </div>
      </div>
    </div>
  );
};

const mapDucksToProps = ({page, location, dateFilter}) => {
  return {
    /** This is the report data that we are showing on the screen */
    billingReports: filterDuck(billingReports, {
      ...dateFilter.queryParams,
      ...queryString.parse(location.search),
      page: page || 1
    }),

    /** The other data we want about the report, like filters, columns, and setup */
    exportableReportConfigs: singleDuck(exportableReportConfigs, `report_${queryString.parse(location.search).report}`),

    /** list of billing reports the user can choose from */
    allBillingReports: filterDuck(exportableReportConfigs, 'all')
  };
};

const mapDispatchToProps = (dispatch, props) => ({
  ...props,
  setFilters: (newFilters) => {
    props.setFilters(newFilters);
    dispatch(billingReports.actions.markDirty());
  }
});

export default compose(
  withRouter,
  withFiltering,
  withPagination,
  withDateFilter(),
  duckify(mapDucksToProps, () => ({}), mapDispatchToProps)
)(BillingReport);
